import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, RefreshCw, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "never");

export function SheetLinkCard({ initialUrl, lastSync, onSynced, api, headers }) {
  const [url, setUrl] = useState(initialUrl);
  const [busy, setBusy] = useState(false);
  useEffect(() => setUrl(initialUrl), [initialUrl]);

  const saveAndSync = async () => {
    setBusy(true);
    try {
      await axios.put(`${api}/sheet-link`, { url }, { headers: headers() });
      if (url.trim()) {
        const { data } = await axios.post(`${api}/sync`, {}, { headers: headers() });
        if (data.ok) toast.success(`Synced from Google Sheet: ${data.updated} updated, ${data.skipped} skipped`);
        else toast.error(`Sync failed: ${data.error}`);
      } else {
        toast.success("Sheet link removed");
      }
      onSynced();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not sync sheet");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass rounded-xl p-6" data-testid="sheet-link-card">
      <div className="flex items-center gap-2 mb-1">
        <Link2 className="w-4 h-4 text-ib-blue" />
        <h3 className="font-outfit font-bold text-sm text-ib-text">Linked Google Sheet</h3>
      </div>
      <p className="font-manrope text-xs text-ib-text-muted mb-4">
        Download the CSV, import it into a Google Sheet, then share it as <span className="text-ib-text-secondary">"Anyone with the link can view"</span> and paste the link here.
        Keep the <span className="font-jetbrains">id</span> and <span className="font-jetbrains">price</span> columns — we re-sync automatically every 24h, or on demand.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input data-testid="sheet-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/..."
          className="bg-ib-surface border-ib-steel/30 text-ib-text placeholder:text-ib-text-muted font-jetbrains text-xs" />
        <Button data-testid="sync-sheet-btn" onClick={saveAndSync} disabled={busy} size="sm" className="bg-ib-lime/90 hover:bg-ib-lime text-ib-bg font-outfit text-xs gap-1.5 shrink-0">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Save & sync now
        </Button>
      </div>
      <div className="mt-3 flex items-center gap-2 font-jetbrains text-[10px] text-ib-text-muted" data-testid="sheet-last-sync">
        {lastSync?.ok === false ? <AlertTriangle className="w-3 h-3 text-ib-warning" /> : <CheckCircle2 className="w-3 h-3 text-ib-lime" />}
        Last sync: {fmt(lastSync?.at)}{lastSync ? ` · ${lastSync.ok ? `${lastSync.updated} updated` : String(lastSync.error).split("\n")[0].slice(0, 120)}` : ""}
      </div>
    </div>
  );
}
